import _ from 'lodash';
import type { Zone } from 'luxon';
import { DateTime, IANAZone, Interval, Settings } from 'luxon';

import {
  CELL_DURATION,
  DAY_CELLS,
  START_HOUR,
  START_MINUTE,
} from '@/components/modules/mentor/calendar/MentorCalendar/parts/consts';
import { HalfHourType } from '@/components/modules/mentor/calendar/MentorCalendar/parts/types';
import { SYSTEM_DEFAULT_TZ } from '@/consts/common';
import type {
  CellCoordinates,
  TimeRange,
} from '@/components/modules/mentor/calendar/MentorCalendar/parts/types';
import type { Weekday } from '@/enums/dates';
import type { WeekBeginEnd } from '@/types/common';

export class DateHelpers {
  /**
   * Takes two dates and generate list of week from-to (Monday to Sunday) ranges
   * covering the given period.
   *
   * E.g. for "24.09.2024 - 31.09.2024" the following list of pairs will be returned:
   *
   *   * 23.09.2024 - 29.09.2024 (starting from Monday)
   *   * 30.09.2024 - 06.10.2024 (ending with Sunday)
   *
   */
  static convertDateRangeToWeeks = (
    from: string,
    to: string,
    tz: string,
  ): WeekBeginEnd[] => {
    let fromDt = DateTime.fromISO(from, { zone: tz });

    // If 'from' day is Saturday or Sunday - start with next week
    if (fromDt.weekday > 5) {
      fromDt = fromDt.plus({ week: 1 }).startOf('week');
    }

    const toDt = DateTime.fromISO(to, { zone: tz });
    const weeksDuration = Math.max(
      // add 52 * YEARS_DELTA as `weekNumber` represents week no within a year,
      // so if `toDt` is in the next year, `weekNumber` may be less than `fromDt` one
      52 * (toDt.weekYear - fromDt.weekYear) +
        toDt.weekNumber -
        fromDt.weekNumber +
        1,
      0,
    );

    return Array(weeksDuration)
      .fill(0)
      .map((_, idx) => {
        const nextWeek = fromDt.plus({ week: idx });
        return {
          begin: nextWeek.startOf('week'),
          end: nextWeek.endOf('week'),
        } as WeekBeginEnd;
      });
  };

  /**
   * Format week start-end dates as "dd-dd.MM" (same month) or as "dd.MM-dd.MM" (different months)
   */
  static formatWeekFromTo = (from: DateTime, to: DateTime): string => {
    const fromFormat = from.get('month') === to.get('month') ? 'dd' : 'dd.MM';

    return `${from.toFormat(fromFormat)}-${to.toFormat('dd.MM')}`;
  };

  static isCurrentWeek = (week: WeekBeginEnd): boolean => {
    const interval = Interval.fromDateTimes(week.begin, week.end);
    return interval.contains(DateTime.now());
  };

  static getCellDatetimeInTimezone = (
    weekStart: DateTime,
    cellCoords: CellCoordinates,
  ) => {
    // First set current day, mentor timezone, then reset hours to 0 and add cell offset (30min)
    // to get cell date/time in mentor tz
    return weekStart.set({ hour: 0, minute: 0 }).plus({
      day: cellCoords.dayIndex,
      minute: START_HOUR * 60 + START_MINUTE + 30 * cellCoords.dayCellIndex,
    });
  };

  /**
   * Converts given date into mentor calendar cell coordinates (respecting mentor tz).
   *
   * E.g.: week 23.09.2024 - 27.09.2024 (Mon-Fri). Week index: 1-5
   * Hours: 08:00 - 20:30 (see `DAY_CELLS`), 26 cells 30 minutes each. Cell indexes: 2-27 (1 - column header)
   *
   * Given times: 24.09.2024 10:00, 24.09.2024 11:00 (mentor tz)
   * Day index: 2 (24 - Tuesday)
   * Cell index: 6 (Cell #5 + 1 offset, first cell is a column header)
   * Span (how many cells to occupy): 11:00 - 10:00 = 60 min duration. Cells: 60 min / CELL_DURATION (30 min) = 2
   *
   * So cell coordinates: {2, 6, 2}
   *
   */
  static getCellCoordinatesForDate = (
    start: DateTime,
    end: DateTime,
  ): CellCoordinates => {
    const dayCellIndex = // First get number of hours from the starting hour (e.g. 8am),
      // multiply by number of cells in hour (if cell duration is 30 minutes, 2 cells in hour).
      // Then add number of `CELL_DURATION` in current hour.
      // E.g. starting hour is 8, current time is 10:45, cell duration is 30 min.
      // 10 - 8 = 2 hours = 4 cells; 45 minutes = 1 cell. So index is 4 + 1 + 1 (header offset) + 1 (next cell)
      (start.hour - START_HOUR) * (60 / CELL_DURATION) +
      Math.floor(start.minute / CELL_DURATION) +
      2;

    return {
      dayIndex: _.inRange(start.weekday, 1, 6) ? start.weekday : 0,
      dayCellIndex: _.inRange(dayCellIndex, 2, DAY_CELLS + 2)
        ? dayCellIndex
        : 0,
      span: Math.round(
        Interval.fromDateTimes(start, end).length('minutes') / CELL_DURATION,
      ),
    };
  };

  /**
   * For the given from/to daterange and from/to time offsets (based on mentor calendar constants, e.g. `START_HOUR`)
   * generates list of slots.
   *
   * For example:
   *
   * 30.09.2024 - 01.10.2024, 08:00 - 09:00 =>
   *   Slot 1: 30.09.2024 08:00
   *   Slot 2: 30.09.2024 08:30
   *   Slot 3: 01.10.2024 08:00
   *   Slot 4: 01.10.2024 08:30
   */
  static generateSlotsFromTimeRange = (
    from: DateTime,
    to: DateTime,
    timeRangeOffsets: TimeRange,
    weekdays: Weekday[],
    mode: HalfHourType,
  ): DateTime[] => {
    const interval = Interval.fromDateTimes(from, to);

    const days = Math.floor(interval.length('days'));

    const firstDay = from.startOf('day').set({
      hour: START_HOUR,
      minute: START_MINUTE * (timeRangeOffsets.fromOffset + 1),
    });

    const numberOfSlots = Math.floor(
      (timeRangeOffsets.toOffset - timeRangeOffsets.fromOffset) /
        (mode === HalfHourType.HALF ? 1 : 2),
    );

    if (numberOfSlots <= 0) {
      return [];
    }

    return Array(days + 1)
      .fill(0)
      .flatMap((_, index) => {
        const day = firstDay.plus({ day: index });
        if (!weekdays.some((w) => w === day.weekday)) {
          return null;
        }

        return Array(numberOfSlots)
          .fill(0)
          .map((_, slotIndex) => {
            const slot = day.plus({
              minute:
                CELL_DURATION *
                (mode === HalfHourType.HALF ? 1 : 2) *
                (slotIndex + timeRangeOffsets.fromOffset),
            });

            if (!interval.contains(slot)) {
              return null;
            }

            return slot;
          });
      })
      .filter(Boolean) as DateTime[];
  };

  static getSystemTimezone(): Zone {
    const defaultZone = Settings.defaultZone;
    return defaultZone.isValid
      ? defaultZone
      : IANAZone.create(SYSTEM_DEFAULT_TZ);
  }

  static getOffsetNameForZone(zone: Zone): string {
    const ts = DateTime.now().toMillis();
    return `GMT${zone.formatOffset(ts, 'short')}`;
  }
}
